/**
 * Hooks: Microsoft Teams transcript pull (Teams Phase B, recruiter-facing).
 *
 * Backend: `backend/app/api/teams_transcripts.py`.
 *
 *   GET  /teams/transcripts/availability  → should we offer the picker at all?
 *   GET  /teams/transcripts               → the caller's own recent transcripts
 *   POST /teams/transcripts/import        → import one → runs the analysis
 *
 * ## Why a dedicated availability probe
 *
 * The obvious move is `useTeamsConnection()` (`hooks/queries/admin/
 * useTeamsConnection.ts`). It does not work here: that endpoint is gated on
 * the `integrations_manage` capability, which admin/owner hold and **recruiter
 * does not** — and a recruiter is exactly who this feature is for. Calling it
 * from the candidate page would 403 for the majority of users and hide the
 * button from the people meant to use it. The availability probe is the
 * member-readable equivalent and returns booleans only, never the Microsoft
 * tenant GUID.
 *
 * ## Import returns the paste-box row
 *
 * `POST /teams/transcripts/import` returns `InterviewAnalysisPasteBoxResponse`
 * — the identical model `paste-box-with-parse` and `paste-box-upload` return,
 * because it runs the identical orchestrator. So this hook seeds the identical
 * cache key `useSubmitTranscript` seeds, and the tab's existing
 * `analysis-loaded` state resolves with no new terminal state. An import is
 * indistinguishable from a paste, by construction.
 *
 * Credits: one `ANALYSIS_PARSE` debit, same as a paste or a file upload. The
 * import route makes no model call of its own.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { apiClient } from "@/lib/apiClient";
import { ApiError } from "@/lib/apiError";
import { useToastStore } from "@/stores/useToastStore";

import type { AnalysisPasteBoxRow } from "./useAnalysisPasteBox";

// ── Wire shapes (mirror the backend serializers) ─────────────────────────────
//
// Only the two shapes a consumer actually names — `TeamsParticipant` and
// `TeamsTranscriptRow` — are exported. The rest are local: they appear only
// as generics on the hooks below, and exporting them would be dead surface
// (`npm run test:deadcode` fails the build on unused exports).

type TeamsUnavailableReason =
  | "not_configured"
  | "not_connected"
  | "tenant_id_missing"
  | "consent_pending";

interface TeamsAvailability {
  available: boolean;
  reason: TeamsUnavailableReason | null;
}

export interface TeamsParticipant {
  /**
   * Frequently `null`. Graph omits `identity.user.displayName` under app-only
   * auth, so this is the common case rather than an edge case — every consumer
   * must render a fallback rather than a blank.
   */
  display_name: string | null;
  role: string | null;
}

export interface TeamsTranscriptRow {
  transcript_id: string;
  meeting_id: string;
  subject: string | null;
  start_at: string | null;
  end_at: string | null;
  created_at: string | null;
  participants: TeamsParticipant[];
  participant_count: number;
  /** Meeting metadata could not be resolved; the row is still importable. */
  metadata_unavailable: boolean;
}

interface TeamsTranscriptList {
  items: TeamsTranscriptRow[];
  lookback_days: number;
  organizer_display_name: string | null;
  /** `no_transcripts_in_window` when the list is empty. NOT an error. */
  empty_reason: string | null;
  truncated: boolean;
}

interface ImportTeamsTranscriptArgs {
  candidate_id: string;
  jd_id: string | undefined;
  stage_id: string;
  /** Per-round slot (backend migration 0124). `null`/undefined = unassigned. */
  round_id?: string | null;
  meeting_id: string;
  transcript_id: string;
}

// ── GET /teams/transcripts/availability ──────────────────────────────────────

/**
 * Is the Teams picker usable by this tenant?
 *
 * Cheap and DB-only on the server (no Graph round trip), so it is safe to fire
 * on every candidate-page analysis tab. Cached generously: the answer changes
 * only when an admin reconfigures the integration.
 */
export function useTeamsAvailability(
  enabled: boolean = true,
): UseQueryResult<TeamsAvailability, ApiError> {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();

  return useQuery<TeamsAvailability, ApiError>({
    queryKey: ["teams-transcripts", "availability", tenantSlug],
    queryFn: async () => {
      const r = await apiClient.get<TeamsAvailability>(
        `/api/t/${tenantSlug}/teams/transcripts/availability`,
      );
      return r.data;
    },
    enabled: !!tenantSlug && enabled,
    staleTime: 5 * 60_000,
    // A failed probe means "don't offer the button" — never a retry storm on a
    // surface the user did not ask for.
    retry: false,
  });
}

// ── GET /teams/transcripts ───────────────────────────────────────────────────

/**
 * The caller's own recent transcribed meetings.
 *
 * Only fires while the picker is open (`enabled`) — it is a live Graph call,
 * not something to run on page load. Not retried: every failure mode is
 * actionable and the message must be shown, not silently re-attempted.
 */
export function useTeamsTranscripts(
  enabled: boolean,
): UseQueryResult<TeamsTranscriptList, ApiError> {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();

  return useQuery<TeamsTranscriptList, ApiError>({
    queryKey: ["teams-transcripts", "list", tenantSlug],
    queryFn: async () => {
      const r = await apiClient.get<TeamsTranscriptList>(
        `/api/t/${tenantSlug}/teams/transcripts`,
      );
      return r.data;
    },
    enabled: !!tenantSlug && enabled,
    retry: false,
    // Graph is slow and the answer is stable for a session's purposes.
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });
}

// ── POST /teams/transcripts/import ───────────────────────────────────────────

/**
 * Import one transcript onto a candidate and run the analysis.
 *
 * Seeds the SAME paste-box cache entry `useSubmitTranscript` seeds, so the
 * Interview Analysis tab transitions to `analysis-loaded` through its existing
 * state machine.
 */
export function useImportTeamsTranscript(): UseMutationResult<
  AnalysisPasteBoxRow,
  ApiError,
  ImportTeamsTranscriptArgs
> {
  const queryClient = useQueryClient();
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const addToast = useToastStore((s) => s.addToast);

  return useMutation<AnalysisPasteBoxRow, ApiError, ImportTeamsTranscriptArgs>({
    mutationFn: async (args) => {
      const r = await apiClient.post<AnalysisPasteBoxRow>(
        `/api/t/${tenantSlug}/teams/transcripts/import`,
        {
          candidate_id: args.candidate_id,
          jd_id: args.jd_id ?? null,
          stage_id: args.stage_id,
          round_id: args.round_id ?? null,
          meeting_id: args.meeting_id,
          transcript_id: args.transcript_id,
        },
      );
      return {
        ...r.data,
        saved_at: r.data.saved_at ?? new Date().toISOString(),
      };
    },
    onSuccess: (data, vars) => {
      queryClient.setQueryData(
        [
          "interview-analysis",
          "paste-box",
          tenantSlug,
          vars.candidate_id,
          vars.jd_id,
          vars.stage_id,
          vars.round_id ?? null,
        ],
        data,
      );
    },
    onError: (err) => {
      if (!(err instanceof ApiError)) return;
      if (err.status === 402) {
        addToast("Out of credits — top up to continue.", "warning");
        return;
      }
      // Every teams_* error carries an actionable, human-written message from
      // the server (the access-policy case in particular names the fix). Show
      // it verbatim rather than inventing a generic one here — one copy of
      // that wording, on the server, is the point.
      if (err.error.startsWith("teams_")) {
        addToast(err.message, "error");
        return;
      }
      if (err.error === "transcript_empty") {
        addToast(
          "That Teams transcript is empty — there was no speech to analyze.",
          "warning",
        );
        return;
      }
      // Anything else bubbles to the consumer's onError.
    },
  });
}
